import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Upload, X, Sparkles, Wand2, Download, Lock, Fingerprint, Package, Layers, Clapperboard, RefreshCw, History, GitCompare, RotateCcw, Save } from "lucide-react";
import { HoloCard, NeonButton, accentColor } from "@/components/Holo";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TYPES = ["TikTok Campaign", "Commercial", "Fashion Campaign", "Movie Scene", "Product Ad", "Social Media"];

const SHOT_PRESETS = {
  "TikTok Campaign": ["Hook shot — model looking into camera", "Product reveal close-up", "Lifestyle action shot", "Call-to-action end frame"],
  "Commercial": ["Establishing wide shot", "Hero product close-up", "Model using the product", "Brand logo end card"],
  "Fashion Campaign": ["Full-body editorial pose", "Close-up beauty shot", "Dynamic movement shot", "Accessory detail shot"],
  "Movie Scene": ["Wide establishing shot", "Character close-up", "Over-the-shoulder shot", "Dramatic reaction shot"],
  "Product Ad": ["Product on podium", "Product in use", "Ingredient / detail macro", "Lifestyle context shot"],
  "Social Media": ["Scroll-stopping hero", "Product flatlay", "UGC-style selfie", "Promo end frame"],
};

const REGEN_MODES = [
  { key: "exact", label: "Regenerate Exact Shot" },
  { key: "variation", label: "Slight Variation" },
  { key: "new_pose", label: "New Pose" },
  { key: "new_expression", label: "New Facial Expression" },
  { key: "new_angle", label: "New Camera Angle" },
  { key: "fix_hands", label: "Fix Hands" },
  { key: "fix_product", label: "Fix Product Placement" },
  { key: "fix_background", label: "Fix Background Elements" },
];
const REGEN_COST = 5;

const fileToDataUrl = (file) => new Promise((res, rej) => {
  const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
});
const download = (dataUrl, name) => {
  const a = document.createElement("a"); a.href = dataUrl; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
};

const LockPicker = ({ label, icon: Icon, items, selected, onSelect, accent }) => {
  const c = accentColor(accent);
  return (
    <div>
      <span className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50 flex items-center gap-1.5"><Icon size={12} /> {label}</span>
      <div className="flex flex-wrap gap-2 mt-2">
        {items.length === 0 && <span className="font-mono-tech text-[11px] text-white/30">None in vault — add in {label}.</span>}
        {items.map((it) => (
          <button key={it.id} type="button" onClick={() => onSelect(selected === it.id ? null : it.id)} data-testid={`lock-${label}-${it.id}`}
            className="flex items-center gap-2 font-mono-tech text-[10px] uppercase px-2 py-1.5 border transition-colors"
            style={selected === it.id ? { borderColor: c.hex, color: c.hex, boxShadow: `0 0 10px rgba(${c.rgb},0.3)` } : { borderColor: "rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.55)" }}>
            {it.image && <img src={it.image} alt="" className="w-5 h-5 object-cover" />} {it.name}
            {selected === it.id && <Lock size={10} />}
          </button>
        ))}
      </div>
    </div>
  );
};

function CampaignDetail({ campaign, onClose, onChanged }) {
  const [camp, setCamp] = useState(campaign);
  const [versions, setVersions] = useState([]);
  const [regenIndex, setRegenIndex] = useState(null);
  const [regenLoading, setRegenLoading] = useState(null);
  const [savingV, setSavingV] = useState(false);
  const [compare, setCompare] = useState(null); // {label, assets}
  const { refreshUser } = useAuth();

  const loadVersions = useCallback(async () => {
    try { const { data } = await api.get(`/campaigns/${camp.id}/versions`); setVersions(data); }
    catch (e) { console.error("Version history load failed:", e); }
  }, [camp.id]);
  useEffect(() => { loadVersions(); }, [loadVersions]);

  const apply = (c) => { setCamp(c); onChanged && onChanged(c); };

  const regenerate = async (index, mode) => {
    setRegenLoading(index);
    try {
      const { data } = await api.post(`/campaigns/${camp.id}/regenerate`, { asset_index: index, mode });
      apply(data.campaign);
      await refreshUser();
      if (data.status === "failed") toast.warning("Regeneration failed — Credit Shield refunded, original frame kept");
      else toast.success("Frame regenerated · continuity locks preserved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRegenLoading(null); setRegenIndex(null); }
  };

  const saveVersion = async () => {
    setSavingV(true);
    try { await api.post(`/campaigns/${camp.id}/versions`, {}); toast.success("Version saved"); loadVersions(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingV(false); }
  };
  const restoreVersion = async (vid) => {
    try { const { data } = await api.post(`/campaigns/${camp.id}/versions/${vid}/restore`); apply(data.campaign); toast.success("Version restored"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const openCompare = async (vid, label) => {
    try { const { data } = await api.get(`/campaigns/${camp.id}/versions/${vid}`); setCompare({ label, assets: data.assets }); }
    catch (e) { toast.error("Could not load version"); }
  };

  const assets = camp.assets || [];

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8" onClick={onClose} data-testid="campaign-detail">
      <div className="glass clip-corner border border-white/10 max-w-6xl w-full max-h-[90vh] overflow-y-auto p-6" style={{ borderTop: "1px solid rgba(176,38,255,0.6)" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-2">
          <div>
            <h2 className="font-display font-bold text-2xl">{camp.name}</h2>
            <p className="font-mono-tech text-[11px] uppercase tracking-widest text-neon-purple mt-1">{camp.type} · {assets.length} scenes · consistency locked</p>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-neon-pink transition-colors" data-testid="close-detail"><X size={20} /></button>
        </div>
        <p className="font-mono-tech text-[11px] text-neon-turquoise/80 italic mb-5">"One bad frame should never destroy an entire campaign."</p>

        <div className="flex flex-wrap gap-2 mb-6 font-mono-tech text-[9px] uppercase">
          {camp.identity_id && <span className="border border-neon-pink/40 text-neon-pink px-2 py-0.5">Identity Locked</span>}
          {camp.product_id && <span className="border border-neon-turquoise/40 text-neon-turquoise px-2 py-0.5">Product Locked</span>}
          {camp.scene_id && <span className="border border-neon-green/40 text-neon-green px-2 py-0.5">Scene Locked</span>}
          {camp.mood && <span className="border border-white/15 text-white/60 px-2 py-0.5">Mood: {camp.mood}</span>}
          {camp.camera_style && <span className="border border-white/15 text-white/60 px-2 py-0.5">Cam: {camp.camera_style}</span>}
        </div>

        {/* VERSION HISTORY */}
        <div className="border border-white/10 p-4 mb-6" data-testid="version-history">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-white/60 flex items-center gap-2"><History size={13} /> Version History</span>
            <NeonButton accent="turquoise" onClick={saveVersion} disabled={savingV} data-testid="save-version-btn"><Save size={12} /> {savingV ? "Saving…" : "Save Version"}</NeonButton>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {versions.length === 0 && <span className="font-mono-tech text-[11px] text-white/30">No versions saved yet. Save one before experimenting so you never lose a good result.</span>}
            {versions.map((v) => (
              <div key={v.id} className="flex items-center gap-2 border border-white/15 px-2.5 py-1.5 font-mono-tech text-[10px]" data-testid={`version-${v.id}`}>
                <span className="text-white/70">{v.label}</span>
                <span className="text-white/30">· {v.shot_count} shots</span>
                <button onClick={() => openCompare(v.id, v.label)} title="Compare" className="text-white/40 hover:text-neon-turquoise transition-colors" data-testid={`compare-${v.id}`}><GitCompare size={12} /></button>
                <button onClick={() => restoreVersion(v.id)} title="Restore" className="text-white/40 hover:text-neon-green transition-colors" data-testid={`restore-${v.id}`}><RotateCcw size={12} /></button>
              </div>
            ))}
          </div>
        </div>

        {/* CAMPAIGN TIMELINE */}
        <div className="mb-6" data-testid="campaign-timeline">
          <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-neon-purple">// Campaign Timeline™ · click a scene to regenerate</span>
          <div className="flex items-stretch gap-0 mt-3 overflow-x-auto pb-2">
            {assets.map((a, i) => (
              <div key={i} className="flex items-center shrink-0">
                <button onClick={() => setRegenIndex(i)} data-testid={`timeline-node-${i}`}
                  className="relative w-24 group">
                  <div className="aspect-square border border-white/15 overflow-hidden group-hover:border-neon-purple transition-colors relative">
                    {a.image ? <img src={a.image} alt="" className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center text-neon-pink font-mono-tech text-[8px] text-center px-1">FAILED</div>}
                    {regenLoading === i && <div className="absolute inset-0 bg-black/70 flex items-center justify-center"><div className="w-6 h-6 border-2 border-neon-turquoise border-t-transparent rounded-full animate-spin" /></div>}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"><RefreshCw size={16} className="text-neon-turquoise" /></div>
                  </div>
                  <div className="font-mono-tech text-[9px] uppercase tracking-widest text-white/50 mt-1.5">Scene {i + 1}</div>
                </button>
                {i < assets.length - 1 && <div className="w-6 h-px bg-neon-purple/40 mx-1 mt-[-18px]" />}
              </div>
            ))}
          </div>
        </div>

        {/* ASSET GRID */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {assets.map((a, i) => (
            <div key={i} className="border border-white/10" data-testid={`asset-${i}`}>
              <div className="aspect-square bg-black/40 overflow-hidden relative group">
                {a.image ? (
                  <>
                    <img src={a.image} alt={a.shot} className="w-full h-full object-cover" />
                    <div className="absolute bottom-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => setRegenIndex(i)} className="bg-black/70 border border-white/20 p-2 text-neon-turquoise" title="Regenerate" data-testid={`regen-btn-${i}`}><RefreshCw size={14} /></button>
                      <button onClick={() => download(a.image, `${camp.name}-scene-${i + 1}.png`)} className="bg-black/70 border border-white/20 p-2 text-white/80" title="Download" data-testid={`download-${i}`}><Download size={14} /></button>
                    </div>
                  </>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-3 text-center font-mono-tech text-[10px] text-neon-pink px-3">
                    FAILED · CREDIT SHIELD REFUNDED
                    <button onClick={() => setRegenIndex(i)} className="border border-neon-turquoise/50 text-neon-turquoise px-3 py-1.5 uppercase tracking-widest hover:bg-neon-turquoise/10 transition-colors" data-testid={`regen-btn-${i}`}><RefreshCw size={11} className="inline mr-1" /> Retry</button>
                  </div>
                )}
                {regenLoading === i && <div className="absolute inset-0 bg-black/70 flex items-center justify-center"><div className="w-8 h-8 border-2 border-neon-turquoise border-t-transparent rounded-full animate-spin" /></div>}
              </div>
              <div className="p-2.5 font-mono-tech text-[10px] text-white/50">Scene {String(i + 1).padStart(2, "0")} · {a.shot}</div>
            </div>
          ))}
        </div>
      </div>

      {/* REGEN MODE PANEL */}
      {regenIndex !== null && (
        <div className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4" onClick={(e) => { e.stopPropagation(); if (regenLoading === null) setRegenIndex(null); }} data-testid="regen-panel">
          <div className="glass clip-corner border border-white/10 max-w-lg w-full p-6" style={{ borderTop: "1px solid rgba(0,240,255,0.6)" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-display font-bold text-lg">Regenerate Scene {regenIndex + 1}</h3>
                <p className="font-mono-tech text-[11px] text-white/45 mt-1">All memory locks stay active — identity, clothing, product, background, lighting, camera & color grading.</p>
              </div>
              <button onClick={() => setRegenIndex(null)} className="text-white/50 hover:text-neon-pink transition-colors"><X size={18} /></button>
            </div>
            <div className="font-mono-tech text-[10px] uppercase tracking-widest text-neon-yellow mt-4">Est. cost: {REGEN_COST} credits</div>
            <div className="grid grid-cols-2 gap-2.5 mt-3">
              {REGEN_MODES.map((m) => (
                <button key={m.key} disabled={regenLoading !== null} onClick={() => regenerate(regenIndex, m.key)} data-testid={`regen-mode-${m.key}`}
                  className="font-mono-tech text-[11px] uppercase tracking-wide border border-white/15 px-3 py-3 text-white/70 hover:border-neon-turquoise hover:text-neon-turquoise transition-colors disabled:opacity-40 text-left">
                  {m.label}
                </button>
              ))}
            </div>
            {regenLoading !== null && <div className="font-mono-tech text-[11px] text-neon-turquoise mt-4 flex items-center gap-2"><Wand2 size={13} className="animate-pulse-glow" /> Regenerating with locks intact…</div>}
          </div>
        </div>
      )}

      {/* COMPARE PANEL */}
      {compare && (
        <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4" onClick={(e) => { e.stopPropagation(); setCompare(null); }} data-testid="compare-panel">
          <div className="glass clip-corner border border-white/10 max-w-4xl w-full max-h-[88vh] overflow-y-auto p-6" style={{ borderTop: "1px solid rgba(0,240,255,0.6)" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display font-bold text-lg">Compare · Current vs {compare.label}</h3>
              <button onClick={() => setCompare(null)} className="text-white/50 hover:text-neon-pink transition-colors"><X size={18} /></button>
            </div>
            <div className="space-y-4">
              {assets.map((a, i) => (
                <div key={i} className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="font-mono-tech text-[9px] uppercase text-neon-green mb-1">Current · Scene {i + 1}</div>
                    <div className="aspect-square border border-white/10 overflow-hidden">{a.image ? <img src={a.image} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-white/20 font-mono-tech text-[10px]">—</div>}</div>
                  </div>
                  <div>
                    <div className="font-mono-tech text-[9px] uppercase text-white/50 mb-1">{compare.label}</div>
                    <div className="aspect-square border border-white/10 overflow-hidden">{compare.assets[i]?.image ? <img src={compare.assets[i].image} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-white/20 font-mono-tech text-[10px]">—</div>}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function WorldForge() {
  const [items, setItems] = useState([]);
  const [vault, setVault] = useState({ character: [], product: [], scene: [] });
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [generating, setGenerating] = useState(false);
  const fileRef = useRef();
  const { refreshUser } = useAuth();

  const [name, setName] = useState("");
  const [type, setType] = useState(TYPES[0]);
  const [mood, setMood] = useState("");
  const [camera, setCamera] = useState("");
  const [styleNotes, setStyleNotes] = useState("");
  const [identityId, setIdentityId] = useState(null);
  const [productId, setProductId] = useState(null);
  const [sceneId, setSceneId] = useState(null);
  const [refs, setRefs] = useState([]);
  const [shots, setShots] = useState([]);
  const [shotInput, setShotInput] = useState("");

  const loadAll = useCallback(async () => {
    try {
      const [c, ch, pr, sc] = await Promise.all([
        api.get("/campaigns"), api.get("/vault?kind=character"), api.get("/vault?kind=product"), api.get("/vault?kind=scene"),
      ]);
      setItems(c.data);
      setVault({ character: ch.data, product: pr.data, scene: sc.data });
    } catch (e) { console.error("World Forge load failed:", e); }
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const resetBuilder = () => {
    setName(""); setType(TYPES[0]); setMood(""); setCamera(""); setStyleNotes("");
    setIdentityId(null); setProductId(null); setSceneId(null); setRefs([]); setShots([]); setShotInput("");
  };

  const pickRefs = async (e) => {
    const files = Array.from(e.target.files || []);
    const urls = await Promise.all(files.map(fileToDataUrl));
    setRefs((p) => [...p, ...urls].slice(0, 5));
  };
  const addShot = (s) => { const v = (s ?? shotInput).trim(); if (!v) return; setShots((p) => [...p, v]); setShotInput(""); };
  const addPresets = () => setShots((p) => [...new Set([...p, ...(SHOT_PRESETS[type] || [])])]);

  const forge = async () => {
    if (!name.trim()) return toast.error("Campaign name required");
    if (shots.length === 0) return toast.error("Add at least one shot");
    setGenerating(true);
    try {
      const { data: camp } = await api.post("/campaigns", {
        name, type, description: styleNotes, mood, camera_style: camera, style_notes: styleNotes,
        identity_id: identityId, product_id: productId, scene_id: sceneId,
      });
      const { data } = await api.post(`/campaigns/${camp.id}/generate`, {
        shots, mood, camera_style: camera, style_notes: styleNotes,
        identity_id: identityId, product_id: productId, scene_id: sceneId,
        extra_reference_images: refs.map((r) => r.split(",")[1]),
      });
      await refreshUser();
      const failed = data.campaign.assets.filter((a) => a.status === "failed").length;
      if (failed) toast.warning(`Campaign forged — ${failed} shot(s) failed, Credit Shield refunded`);
      else toast.success("Campaign forged with locked consistency");
      setOpen(false); resetBuilder(); loadAll(); setActive(data.campaign);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setGenerating(false); }
  };

  const remove = async (id) => { await api.delete(`/campaigns/${id}`); setItems((p) => p.filter((i) => i.id !== id)); };
  const cost = shots.length * 5;

  return (
    <div data-testid="module-worldforge">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="font-display font-bold text-2xl sm:text-3xl">World Forge</h1>
          <p className="font-mono-tech text-white/40 text-sm mt-1">Generate entire campaigns with locked face · product · scene consistency</p>
        </div>
        <NeonButton accent="purple" onClick={() => { setOpen(!open); resetBuilder(); }} data-testid="campaign-add-btn"><Plus size={14} /> New Campaign</NeonButton>
      </div>

      {open && (
        <HoloCard accent="purple" className="p-6 mb-8" hover={false} testid="campaign-builder">
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">Campaign Name</span>
                  <input value={name} onChange={(e) => setName(e.target.value)} data-testid="campaign-name-input"
                    className="mt-2 w-full bg-black/30 border-b border-white/20 focus:border-neon-purple focus:outline-none px-1 py-2 text-white font-mono-tech text-sm" />
                </div>
                <div>
                  <span className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">Type</span>
                  <select value={type} onChange={(e) => setType(e.target.value)} data-testid="campaign-type-select"
                    className="mt-2 w-full bg-black/30 border border-white/15 focus:border-neon-purple focus:outline-none px-2 py-2 text-white font-mono-tech text-sm">
                    {TYPES.map((t) => <option key={t} className="bg-[#0d0d0f]">{t}</option>)}
                  </select>
                </div>
                <div>
                  <span className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">Mood</span>
                  <input value={mood} onChange={(e) => setMood(e.target.value)} placeholder="luxury, moody, vibrant" data-testid="campaign-mood-input"
                    className="mt-2 w-full bg-black/30 border-b border-white/20 focus:border-neon-purple focus:outline-none px-1 py-2 text-white font-mono-tech text-sm" />
                </div>
                <div>
                  <span className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">Camera Style</span>
                  <input value={camera} onChange={(e) => setCamera(e.target.value)} placeholder="35mm, cinematic, macro" data-testid="campaign-camera-input"
                    className="mt-2 w-full bg-black/30 border-b border-white/20 focus:border-neon-purple focus:outline-none px-1 py-2 text-white font-mono-tech text-sm" />
                </div>
              </div>
              <div>
                <span className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">Style Notes (fonts, clothing, extras)</span>
                <input value={styleNotes} onChange={(e) => setStyleNotes(e.target.value)} data-testid="campaign-style-input"
                  className="mt-2 w-full bg-black/30 border-b border-white/20 focus:border-neon-purple focus:outline-none px-1 py-2 text-white font-mono-tech text-sm" />
              </div>
              <div className="space-y-4 pt-2 border-t border-white/10">
                <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-neon-purple">// Consistency Locks</p>
                <LockPicker label="Identity" icon={Fingerprint} items={vault.character} selected={identityId} onSelect={setIdentityId} accent="pink" />
                <LockPicker label="Product" icon={Package} items={vault.product} selected={productId} onSelect={setProductId} accent="turquoise" />
                <LockPicker label="Scene" icon={Layers} items={vault.scene} selected={sceneId} onSelect={setSceneId} accent="green" />
                <div>
                  <span className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50 flex items-center gap-1.5"><Upload size={12} /> Extra Reference Images</span>
                  <div className="flex flex-wrap gap-2 mt-2 items-center">
                    {refs.map((r, i) => (
                      <div key={i} className="relative w-14 h-14 border border-white/20">
                        <img src={r} alt="" className="w-full h-full object-cover" />
                        <button onClick={() => setRefs((p) => p.filter((_, j) => j !== i))} className="absolute -top-2 -right-2 bg-black border border-white/20 text-white/70"><X size={12} /></button>
                      </div>
                    ))}
                    {refs.length < 5 && (
                      <button onClick={() => fileRef.current?.click()} data-testid="campaign-ref-upload" className="w-14 h-14 border border-dashed border-white/25 flex items-center justify-center text-white/40 hover:border-neon-purple transition-colors"><Plus size={16} /></button>
                    )}
                    <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={pickRefs} />
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-neon-purple">// Campaign Shots</p>
                <button onClick={addPresets} data-testid="campaign-preset-btn" className="font-mono-tech text-[10px] uppercase text-neon-turquoise border border-neon-turquoise/40 px-2 py-1 hover:bg-neon-turquoise/10 transition-colors">Load {type.split(" ")[0]} presets</button>
              </div>
              <div className="flex gap-2">
                <input value={shotInput} onChange={(e) => setShotInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addShot()} placeholder="Describe a shot…" data-testid="campaign-shot-input"
                  className="flex-1 bg-black/30 border-b border-white/20 focus:border-neon-purple focus:outline-none px-1 py-2 text-white font-mono-tech text-sm" />
                <NeonButton accent="purple" onClick={() => addShot()} data-testid="campaign-add-shot-btn"><Plus size={13} /></NeonButton>
              </div>
              <div className="space-y-2 max-h-[240px] overflow-y-auto">
                {shots.length === 0 && <div className="font-mono-tech text-white/30 text-xs border border-dashed border-white/10 p-6 text-center">No shots yet. Add shots or load presets.</div>}
                {shots.map((s, i) => (
                  <div key={i} className="flex items-center gap-3 border border-white/10 px-3 py-2 font-mono-tech text-xs text-white/70" data-testid={`shot-${i}`}>
                    <span className="text-neon-purple">{String(i + 1).padStart(2, "0")}</span>
                    <span className="flex-1">{s}</span>
                    <button onClick={() => setShots((p) => p.filter((_, j) => j !== i))} className="text-white/30 hover:text-neon-pink transition-colors"><X size={13} /></button>
                  </div>
                ))}
              </div>
              <div className="pt-3 border-t border-white/10">
                <NeonButton accent="purple" onClick={forge} disabled={generating} className="w-full" data-testid="campaign-forge-btn">
                  {generating ? <><Wand2 size={14} className="animate-pulse-glow" /> Forging {shots.length} consistent shots…</> : <><Sparkles size={14} /> Forge Campaign ({cost} credits)</>}
                </NeonButton>
              </div>
            </div>
          </div>
        </HoloCard>
      )}

      {items.length === 0 && !open ? (
        <div className="font-mono-tech text-white/30 text-sm border border-white/10 border-dashed p-12 text-center">No campaigns forged yet. Lock your assets and generate a consistent campaign.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((c) => (
            <HoloCard key={c.id} accent="purple" className="p-5" testid={`campaign-${c.id}`}>
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 flex items-center justify-center border border-white/15 text-neon-purple"><Clapperboard size={18} strokeWidth={1.5} /></div>
                <button onClick={() => remove(c.id)} data-testid={`del-campaign-${c.id}`} className="text-white/30 hover:text-neon-pink transition-colors"><Trash2 size={14} /></button>
              </div>
              <h3 className="font-display font-bold text-base mt-4">{c.name}</h3>
              <p className="font-mono-tech text-[10px] uppercase tracking-widest text-neon-purple mt-1">{c.type} · {c.shot_count} scenes</p>
              {c.assets?.filter((a) => a.image).length > 0 && (
                <div className="grid grid-cols-3 gap-1.5 mt-4">
                  {c.assets.filter((a) => a.image).slice(0, 6).map((a, i) => (
                    <div key={i} className="aspect-square overflow-hidden border border-white/10"><img src={a.image} alt="" className="w-full h-full object-cover" /></div>
                  ))}
                </div>
              )}
              <NeonButton accent="purple" className="w-full mt-4" onClick={() => setActive(c)} data-testid={`view-campaign-${c.id}`}>Open Campaign</NeonButton>
            </HoloCard>
          ))}
        </div>
      )}

      {active && <CampaignDetail campaign={active} onClose={() => { setActive(null); loadAll(); }} onChanged={(c) => setItems((p) => p.map((x) => x.id === c.id ? { ...x, ...c } : x))} />}
    </div>
  );
}
